import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  ChartBarIcon,
  CpuChipIcon,
  IdentificationIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatDateTime, formatTradingDay, statusBadge, feedStatusColor } from './dailyDataUtils'
import DailyDataNav from './DailyDataNav'

function FeedCard({ feed }) {
  const icons = {
    egm: ServerStackIcon,
    patron: UserGroupIcon,
    visitor: IdentificationIcon,
    sessions: ChartBarIcon,
    meter: CpuChipIcon,
    'slot-analysis': ChartBarIcon,
  }
  const FeedIcon = icons[feed.id] || UserGroupIcon

  if (feed.coming_soon) {
    return (
      <div className={`rounded-lg border p-5 ${feedStatusColor('coming_soon')}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-700">{feed.label}</h3>
            <p className="text-sm text-gray-500 mt-1">{feed.description}</p>
          </div>
          {statusBadge('coming_soon')}
        </div>
        <p className="text-xs text-gray-400 mt-4">Not available for this tenant yet.</p>
      </div>
    )
  }

  const href = feed.id === 'patron'
    ? '/monitor-safe/daily-data/dimpatron'
    : `/monitor-safe/daily-data/${feed.id}`

  return (
    <div className={`rounded-lg border p-5 ${feedStatusColor(feed.status)}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-white/80 p-2 border border-gray-200">
            <FeedIcon className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{feed.label}</h3>
            <p className="text-sm text-gray-600 mt-0.5">{feed.description}</p>
          </div>
        </div>
        {statusBadge(feed.status)}
      </div>

      <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Trading day</dt>
          <dd className="font-medium text-gray-900">{formatTradingDay(feed.trading_day_today)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Last run</dt>
          <dd className="font-medium text-gray-900">{formatDateTime(feed.last_run_at)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Template / source</dt>
          <dd className="font-medium text-gray-900 truncate" title={feed.template_name}>
            {feed.id === 'visitor' && feed.visitor_source === 'api'
              ? 'RXConnect API'
              : (feed.template_name || 'Default')}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase tracking-wide">Schedule</dt>
          <dd className="font-medium text-gray-900 font-mono text-xs">
            {feed.scheduler_enabled ? (feed.schedule || '—') : 'Manual only'}
          </dd>
        </div>
      </dl>

      {feed.last_success && (
        <p className="text-xs text-gray-600 mt-3">
          Last success: {feed.last_success.imported} imported
          {feed.last_success.updated != null && feed.id === 'egm' && `, ${feed.last_success.updated} denoms synced`}
          {feed.last_success.updated != null && feed.id !== 'egm' && `, ${feed.last_success.updated} updated`}
          {feed.last_success.skipped != null && `, ${feed.last_success.skipped} skipped`}
          {feed.last_success.errors > 0 && `, ${feed.last_success.errors} errors`}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={href}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
        >
          Open
          <ArrowRightIcon className="w-4 h-4 ml-1" />
        </Link>
        {feed.id === 'meter' && (
          <Link
            to="/monitor-safe/daily-data/turnover"
            className="inline-flex items-center px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 text-sm font-medium hover:bg-indigo-100"
          >
            Daily turnover
            <ArrowRightIcon className="w-4 h-4 ml-1" />
          </Link>
        )}
      </div>
    </div>
  )
}

function PipelineStatusCard({ pipeline, tradingDay }) {
  if (!pipeline) return null

  const state = pipeline.pipeline_state || 'waiting_ingest'
  const border =
    state === 'rules_ready' ? 'border-green-200 bg-green-50'
      : state === 'running' ? 'border-blue-200 bg-blue-50'
        : (state.includes('failed') || state === 'failed') ? 'border-red-200 bg-red-50'
          : 'border-amber-200 bg-amber-50'

  return (
    <div className={`rounded-lg border p-5 ${border}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Transaction monitoring pipeline</h2>
          <p className="text-sm text-gray-600 mt-1">
            Ingest → enrich → allocate → rules-ready for trading day {formatTradingDay(tradingDay || pipeline.trading_day)}.
          </p>
        </div>
        {statusBadge(state)}
      </div>
      <dl className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 text-xs uppercase">Meter ingest</dt>
          <dd className="font-medium">{pipeline.ingest_meter_ok ? 'OK' : 'Pending'}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Sessions ingest</dt>
          <dd className="font-medium">{pipeline.ingest_sessions_ok ? 'OK' : 'Pending'}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Enriched rows</dt>
          <dd className="font-medium">{pipeline.enriched_count ?? 0}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Attribution rows</dt>
          <dd className="font-medium">{pipeline.attribution_count ?? 0}</dd>
        </div>
        <div>
          <dt className="text-gray-500 text-xs uppercase">Auto pipeline</dt>
          <dd className="font-medium">{pipeline.auto_pipeline_enabled ? 'On' : 'Off'}</dd>
        </div>
      </dl>
      {pipeline.last_error && (
        <p className="text-sm text-red-700 mt-3">{pipeline.last_error}</p>
      )}
      {!pipeline.rules_ready && state !== 'running' && (
        <p className="text-xs text-gray-600 mt-3">
          Run <strong>Process &amp; Combine</strong> on Gaming Data → Combined, or enable auto-pipeline in platform config when ready.
        </p>
      )}
    </div>
  )
}

export default function DailyData() {
  const { can } = useAuth()
  const canManageSettings = can('admin/system', 'view')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/daily-data/overview'))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load Daily Data')
      setData(json.data)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DailyDataNav />
      <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Daily Data</h1>
            <p className="text-gray-600 mt-1">
              Vendor extracts by trading day — DimPatron, gaming sessions, and meter readings.
            </p>
            {data?.trading_day_today && (
              <p className="text-sm text-gray-500 mt-2 inline-flex items-center gap-1">
                <CalendarDaysIcon className="w-4 h-4" />
                Today&apos;s trading day: <span className="font-medium">{formatTradingDay(data.trading_day_today)}</span>
                <span className="text-gray-400">({data.timezone})</span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canManageSettings && (
              <Link
                to="/monitor-safe/daily-data/settings"
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                <Cog6ToothIcon className="w-4 h-4 mr-1" />
                Settings
              </Link>
            )}
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{error}</div>
      )}

      {data?.pipeline && (
        <PipelineStatusCard pipeline={data.pipeline} tradingDay={data.trading_day_today} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(data?.feeds || []).map(feed => (
          <FeedCard key={feed.id} feed={{ ...feed, trading_day_today: data?.trading_day_today }} />
        ))}
      </div>

      {!data?.config && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Import configuration is not provisioned for this tenant. Run migration 010 and configure
          {' '}
          <Link to="/monitor-safe/daily-data/settings" className="underline font-medium">Daily Data settings</Link>.
        </div>
      )}
    </div>
  )
}
